import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';

import { useFhirRealtimeOptional } from '../../../hooks/fhirRealtimeContext';
import { useEffectivePortalPatientId } from '../../../hooks/usePortalSubjectPatientId';
import {
  isClinicalQuestionnaire,
  isClientFacingDocumentQuestionnaire,
  isClientUploadedDocument,
  isClientVisibleDocumentReference,
  isClientFacingTask,
  isRulesEngineFormAssignment,
} from '../utils/clientPortalClassifiers';
import type { ClientPortalDocumentRow } from '../utils/clientPortalFhirTypes';

export interface UseClientPortalDocumentsResult {
  documents: ClientPortalDocumentRow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

async function resolveQuestionnaire(
  medplum: ReturnType<typeof useMedplum>,
  ref: string | undefined,
): Promise<Questionnaire | undefined> {
  if (!ref) return undefined;
  const [rt, id] = ref.split('/');
  if (rt !== 'Questionnaire' || !id) return undefined;
  try {
    return await medplum.readResource('Questionnaire', id);
  } catch {
    return undefined;
  }
}

export function useClientPortalDocuments(): UseClientPortalDocumentsResult {
  const medplum = useMedplum();
  const patientId = useEffectivePortalPatientId();
  const [documents, setDocuments] = useState<ClientPortalDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const loadDocuments = useCallback(async () => {
    if (!patientId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      medplum.invalidateSearches('Task');
      medplum.invalidateSearches('DocumentReference');

      const rows: ClientPortalDocumentRow[] = [];

      // 1. Pending document/signature Tasks (client-facing only, not clinical assessments)
      const pendingTasks = await medplum.searchResources('Task', {
        patient: `Patient/${patientId}`,
        status: 'requested,received,accepted,ready',
        _sort: '-_lastUpdated',
        _count: '100',
      });

      for (const task of pendingTasks) {
        if (!isClientFacingTask(task)) continue;

        const focusRef = task.focus?.reference;
        const [focusType] = focusRef?.split('/') ?? [];

        if (focusType === 'Questionnaire') {
          const q = await resolveQuestionnaire(medplum, focusRef);
          if (isClinicalQuestionnaire(q)) continue;

          // Include if: (a) Questionnaire matches client-facing doc criteria,
          // OR (b) Task is a Rules Engine form assignment (always client-facing)
          const isClientDoc = isClientFacingDocumentQuestionnaire(q);
          const isREFormAssignment = isRulesEngineFormAssignment(task);
          if (!isClientDoc && !isREFormAssignment && !q) continue;
          if (!isClientDoc && !isREFormAssignment) continue;

          const needsSignature = task.description?.toLowerCase().includes('sign') ?? false;
          rows.push({
            id: `task-${task.id}`,
            name: q?.title ?? task.focus?.display ?? task.description ?? 'Form',
            kind: 'Form',
            status: 'Pending',
            date: task.restriction?.period?.end ?? task.authoredOn ?? '',
            action: needsSignature ? 'sign' : 'complete',
            taskId: task.id,
            needsSignature,
          });
        }
      }

      // 2. Client-visible DocumentReferences (uploads + shared files).
      // Submitted QuestionnaireResponses are intentionally not readable in the portal.
      const docRefs = await medplum.searchResources('DocumentReference', {
        subject: `Patient/${patientId}`,
        _sort: '-_lastUpdated',
        _count: '100',
      });

      for (const doc of docRefs) {
        if (!isClientUploadedDocument(doc, patientId) && !isClientVisibleDocumentReference(doc)) {
          continue;
        }

        const docAttachment = doc.content?.[0]?.attachment;
        rows.push({
          id: `doc-${doc.id}`,
          name:
            doc.description ??
            docAttachment?.title ??
            doc.type?.text ??
            'Document',
          kind: 'File',
          status: isClientUploadedDocument(doc, patientId) ? 'Completed' : 'Shared',
          date: doc.date ?? '',
          documentReferenceId: doc.id,
          ...(docAttachment ? {
            attachment: {
              contentType: docAttachment.contentType,
              url: docAttachment.url,
              data: docAttachment.data,
              title: docAttachment.title,
              size: docAttachment.size,
            },
          } : {}),
        });
      }

      setDocuments(rows);
    } catch (err) {
      console.error('[useClientPortalDocuments] Failed to fetch documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, patientId]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments, tick]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const fhirRealtime = useFhirRealtimeOptional();

  useEffect(() => {
    if (!fhirRealtime) return;
    return fhirRealtime.onClientPortalRefresh(() => {
      void loadDocuments();
    });
  }, [fhirRealtime, loadDocuments]);

  return { documents, loading, error, refresh };
}
